import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SafeImage from '@/components/base/SafeImage';
import Modal from '@/components/base/Modal';
import SectionLink from '@/components/feature/SectionLink';
import { useCart } from '@/context/CartContext';
import { useMetaPixel } from '@/context/MetaPixelContext';
import { useProducts } from '@/hooks/useProducts';
import { useInventory } from '@/hooks/useInventory';
import { useActiveDiscounts } from '@/hooks/useActiveDiscounts';
import { getProductDiscountPercent, discountedPrice } from '@/services/discounts';

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface DrawerItem {
  productId: string;
  sizeId: string;
  name: string;
  sizeLabel: string;
  image: string;
  quantity: number;
  finalPrice: number;
  subtotal: number;
  production: boolean;
  // Línea comercial y stock real: la línea sencilla no puede superar el stock.
  lineType: string;
  available: number;
}

// Mini-carrito desplegable: permite revisar, ajustar cantidades y eliminar
// productos desde cualquier página antes de pasar al pago.
export default function CartDrawer({ open, onClose }: CartDrawerProps) {
  const { items, count, removeItem, updateQuantity, clearCart } = useCart();
  const { track } = useMetaPixel();
  const { data: products } = useProducts();
  const { inventory, sizes } = useInventory();
  const discounts = useActiveDiscounts();
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const resolved = useMemo<DrawerItem[]>(
    () =>
      items.map((item) => {
        const product = products.find((p) => p.id === item.productId);
        const size = sizes.find((s) => s.id === item.sizeId);
        const inv = inventory.find(
          (i) => i.productId === item.productId && i.sizeId === item.sizeId,
        );
        const unitPrice = product?.commercialPrice ?? 0;
        const pct = getProductDiscountPercent(item.productId, discounts, product?.lineType);
        const finalPrice = discountedPrice(unitPrice, pct);
        const available = inv ? inv.quantityAvailable : 0;
        const production = item.production === true || available <= 0;
        return {
          productId: item.productId,
          sizeId: item.sizeId,
          name: product?.name ?? 'Producto',
          sizeLabel: size?.sizeRange ? `Talla ${size.sizeRange}` : size?.name ?? '',
          image: product?.images?.[0] ?? '',
          quantity: item.quantity,
          finalPrice,
          subtotal: finalPrice * item.quantity,
          production,
          lineType: product?.lineType ?? 'premium',
          available,
        };
      }),
    [items, products, sizes, inventory, discounts],
  );

  const total = resolved.reduce((sum, r) => sum + r.subtotal, 0);

  // Bloquea el scroll del fondo y cierra con Escape mientras está abierto.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return (
    <>
      <div
        className={`fixed inset-0 z-[60] bg-black/40 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      ></div>

      <aside
        className={`fixed top-0 right-0 z-[70] h-full w-full max-w-md bg-white flex flex-col transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-label="Carrito de compras"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <h2 className="text-base font-semibold text-foreground-950 flex items-center gap-2">
            <i className="ri-shopping-bag-3-line text-accent-600"></i>
            Tu carrito {count > 0 && <span className="text-foreground-500 font-normal">({count})</span>}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Cerrar carrito"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {resolved.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-shopping-bag-3-line text-3xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-700 font-medium mb-1">Tu carrito está vacío</p>
            <p className="text-sm text-foreground-500 mb-6">Agrega unas paticas con propósito.</p>
            <SectionLink
              section="catalogo"
              onClick={onClose}
              className="px-6 py-3 bg-accent-500 text-white rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              Ir al catálogo
            </SectionLink>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {resolved.map((r) => (
                <div
                  key={`${r.productId}-${r.sizeId}`}
                  className="flex gap-3 pb-4 border-b border-background-100 last:border-b-0"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-background-100 flex-shrink-0 flex items-center justify-center">
                    <SafeImage src={r.image} alt={r.name} className="w-full h-full object-cover" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-foreground-900 leading-snug">
                        {r.name}
                        {r.production && (
                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 align-middle whitespace-nowrap">
                            Producción
                          </span>
                        )}
                      </p>
                      <button
                        type="button"
                        onClick={() => removeItem(r.productId, r.sizeId)}
                        className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        aria-label={`Eliminar ${r.name}`}
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                    {r.sizeLabel && <p className="text-xs text-foreground-500 mt-0.5">{r.sizeLabel}</p>}

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => updateQuantity(r.productId, r.sizeId, r.quantity - 1)}
                          className="w-6 h-6 rounded-md bg-background-100 text-foreground-600 hover:bg-background-200 flex items-center justify-center text-xs cursor-pointer"
                          aria-label="Quitar uno"
                        >
                          −
                        </button>
                        <span className="text-sm font-medium text-foreground-900 w-5 text-center">
                          {r.quantity}
                        </span>
                        <button
                          type="button"
                          disabled={r.lineType !== 'premium' && r.available <= r.quantity}
                          onClick={() => {
                            // Línea sencilla: no se puede superar el stock disponible.
                            if (r.lineType !== 'premium' && r.available <= r.quantity) return;
                            const nextQty = r.quantity + 1;
                            updateQuantity(r.productId, r.sizeId, nextQty);
                            // No perder conversiones de quien ajusta cantidades:
                            // cada subida de cantidad cuenta como AddToCart.
                            track('AddToCart', {
                              content_ids: [r.productId],
                              content_name: r.name,
                              content_type: 'product',
                              value: r.finalPrice * nextQty,
                              currency: 'COP',
                              contents: [
                                { id: r.productId, quantity: nextQty, item_price: r.finalPrice },
                              ],
                            });
                          }}
                          className="w-6 h-6 rounded-md bg-background-100 text-foreground-600 hover:bg-background-200 flex items-center justify-center text-xs cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-background-100"
                          aria-label="Agregar uno"
                        >
                          +
                        </button>
                      </div>
                      <span className="text-sm font-semibold text-foreground-950">
                        ${r.subtotal.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-background-200 px-5 py-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-foreground-500">Subtotal</span>
                <span className="text-lg font-bold text-foreground-950">${total.toLocaleString()}</span>
              </div>
              <p className="text-xs text-foreground-400">
                El costo de envío se calcula en el siguiente paso.
              </p>
              <Link
                to="/checkout"
                onClick={onClose}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-accent-500 text-white rounded-xl font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-lock-2-line"></i>
                Finalizar compra
              </Link>
              <button
                type="button"
                onClick={onClose}
                className="w-full py-2.5 text-sm text-foreground-600 hover:text-foreground-900 font-medium transition-colors cursor-pointer"
              >
                Seguir comprando
              </button>
              <button
                type="button"
                onClick={() => setShowClearConfirm(true)}
                className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-red-500 hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-delete-bin-2-line"></i>
                Vaciar carrito
              </button>
            </div>
          </>
        )}
      </aside>

      {showClearConfirm && (
        <Modal
          title="Vaciar carrito"
          subtitle="Se quitarán todos los productos que agregaste."
          onClose={() => setShowClearConfirm(false)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-foreground-600 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  clearCart();
                  setShowClearConfirm(false);
                }}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Sí, vaciar
              </button>
            </>
          }
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-delete-bin-2-line text-red-500 text-lg"></i>
            </div>
            <p className="text-sm text-foreground-700 leading-relaxed">
              ¿Seguro que quieres vaciar por completo tu carrito? Esta acción no se puede deshacer.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}